<template>
  <div class="gestion-biens-container">
    <div class="gestion-biens-header">
      <div class="header-left">
        <img src="@/assets/icons/cle.png" alt="Gestion" class="header-icon" />
        <h1>Gestions des biens</h1>
      </div>
      <div class="header-filters">
        <input 
          v-model="searchProjet"
          type="text" 
          placeholder="Chercher par Projet"
          class="filter-input"
        />
        <div class="type-filters">
          <button 
            class="filter-btn" 
            :class="{ active: selectedType === 'Tout' }"
            @click="selectedType = 'Tout'"
          >Tout</button>
          <button 
            v-for="typeOption in typeOptions" 
            :key="typeOption.value"
            class="filter-btn"
            :class="{ active: selectedType === typeOption.value }"
            @click="selectedType = typeOption.value"
          >{{ typeOption.label }}</button>
        </div>
      </div>
    </div>

    <!-- Message d'erreur -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <div class="status-tabs">
      <button 
        class="tab-btn" 
        :class="{ active: selectedStatus === 'Toutes' }"
        @click="selectedStatus = 'Toutes'"
      >
        Toutes
        <span class="count all">{{ totalItems }}</span>
      </button>
      <button 
        class="tab-btn occupied" 
        :class="{ active: selectedStatus === 'Occupés' }"
        @click="selectedStatus = 'Occupés'"
      >
        Occupés
        <span class="count">{{ biensOccupes.length }}</span>
      </button>
      <button 
        class="tab-btn vacant" 
        :class="{ active: selectedStatus === 'Vacantes' }"
        @click="selectedStatus = 'Vacantes'"
      >
        Vacantes
        <span class="count">{{ biensVacants.length }}</span>
      </button>
    </div>

    <div class="biens-table">
      <div class="table-header">
        <div class="header-cell">ID</div>
        <div class="header-cell">Projet</div>
        <div class="header-cell">Type</div>
        <div class="header-cell">Surface (m²)</div>
        <div class="header-cell">Prix (MAD/mois)</div>
        <div class="header-cell">Adresse</div>
        <div class="header-cell">Ville</div>
        <div class="header-cell">Code Postal</div>
        <div class="header-cell">Status</div>
        <div class="header-cell">Description</div>
        <div class="header-cell">Date Création</div>
      </div>

      <div class="table-body" :class="{ 'loading': isLoading }">
        <div v-if="isLoading" class="loading-overlay">
          <div class="loading-spinner"></div>
        </div>
        
        <template v-if="biens.length > 0">
          <div v-for="bien in biens" :key="bien.id" class="table-row">
            <div class="cell">
              <span class="id-value">{{ bien.id || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="project-name">{{ bien.project || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="type-badge">{{ getTypeLabel(bien.type) || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="surface-value">{{ bien.surface || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="price-value">{{ formatPrice(bien.price) }}</span>
            </div>
            <div class="cell">
              <span class="address-main">{{ bien.address || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="city-value">{{ bien.city || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="postal-value">{{ bien.postal_code || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span :class="['status-badge', bien.status === 'rented' ? 'occupied' : 'vacant']">
                {{ mapStatusToFrontend(bien.status) }}
              </span>
            </div>
            <div class="cell">
              <span class="description-text">{{ bien.description || 'N/A' }}</span>
            </div>
            <div class="cell">
              <span class="date-value">{{ formatDate(bien.created_at) }}</span>
            </div>
          </div>
        </template>
        
        <div v-else class="empty-state">
          Aucun bien trouvé
        </div>

        <!-- Lignes fantômes pour maintenir la hauteur du tableau -->
        <template v-if="biens.length < perPage">
          <div v-for="i in (perPage - biens.length)" :key="'phantom-' + i" class="table-row phantom">
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
            <div class="cell"></div>
          </div>
        </template>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="showPagination" class="pagination-container">
      <div class="pagination-info">
        Page {{ currentPage }} sur {{ lastPage }} ({{ totalItems }} biens)
      </div>
      
      <div class="pagination">
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1 || isLoading"
          @click="changePage(currentPage - 1)"
        >
          Précédent
        </button>
        <div class="page-numbers">
          <button 
            v-for="page in displayedPages" 
            :key="page"
            class="page-number"
            :class="{ active: page === currentPage }"
            :disabled="isLoading"
            @click="changePage(page)"
          >
            {{ page }}
          </button>
        </div>
        <button 
          class="pagination-btn" 
          :disabled="currentPage === lastPage || isLoading"
          @click="changePage(currentPage + 1)"
        >
          Suivant
        </button>
      </div>
    </div>


    <!-- Modal d'ajout de bien -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="showAddModal = false">
      <div class="add-bien-modal">
        <div class="modal-header">
          <h2>Ajouter un bien</h2>
          <button class="close-btn" @click="showAddModal = false">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label>Ref</label>
            <input type="text" v-model="newBien.ref" placeholder="WP_B18" />
          </div>

          <div class="form-group">
            <label>Projet</label>
            <input type="text" v-model="newBien.projet" placeholder="Walili Tower" />
          </div>

          <div class="form-group">
            <label>Type</label>
            <div class="type-buttons">
              <button 
                v-for="typeOption in typeOptions" 
                :key="typeOption.value"
                :class="['type-btn', newBien.type === typeOption.value ? 'active' : '']"
                @click="newBien.type = typeOption.value"
              >
                {{ typeOption.label }}
              </button>
            </div>
          </div>

          <div class="form-group">
            <label>S m²</label>
            <input type="number" v-model="newBien.surface" placeholder="80" />
          </div>

          <div class="form-group">
            <label>Terasse m²</label>
            <input type="number" v-model="newBien.terasse" placeholder="0" />
          </div>

          <div class="form-group">
            <label>Box m²</label>
            <input type="number" v-model="newBien.box" placeholder="-" />
          </div>

          <div class="form-group documents">
            <div class="doc-link">
              <img src="@/assets/icons/addplan.png" alt="Plan" class="doc-icon" />
              <a href="#" @click.prevent="uploadPlan">Voir plan</a>
            </div>
            <div class="doc-link">
              <img src="@/assets/icons/temoin.png" alt="Témoin" class="doc-icon" />
              <a href="#" @click.prevent="uploadTemoin">Voir Témoin</a>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="submit-btn" @click="addBien">Ajouter</button>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import bienService from '@/services/bienService'

// Options de type de bien (same as creation process)
const typeOptions = [
  { value: 'apartment', label: 'Appartement' },
  { value: 'shop', label: 'Magasin' },
  { value: 'office', label: 'Bureau' },
  { value: 'house', label: 'Maison' },
  { value: 'land', label: 'Terrain' }
]

// État des filtres
const searchProjet = ref('')
const selectedType = ref('Tout')
const selectedStatus = ref('Toutes')

// État des données
const biens = ref([])
const isLoading = ref(false)
const error = ref(null)
const showAddModal = ref(false)

const newBien = ref({
  ref: '',
  projet: '',
  type: '',
  surface: null,
  terasse: null,
  box: null,
  plan: null,
  temoin: null
})

// Pagination
const currentPage = ref(1)
const perPage = ref(10)
const totalItems = ref(0)
const lastPage = ref(1)

// Debounce pour la recherche
let searchTimeout = null

// Watcher pour la recherche par projet avec debounce
watch(searchProjet, () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadBiens()
  }, 300)
})

// Watcher pour les filtres de type et statut
watch([selectedType, selectedStatus], () => {
  currentPage.value = 1
  loadBiens()
})

// Computed properties pour les compteurs
const biensOccupes = computed(() => {
  if (!Array.isArray(biens.value)) return []
  return biens.value.filter(b => b.status === 'rented')
})
const biensVacants = computed(() => {
  if (!Array.isArray(biens.value)) return []
  return biens.value.filter(b => b.status === 'available')
})

// Computed property pour les pages à afficher
const displayedPages = computed(() => {
  // Calculer le nombre total de pages nécessaires
  const calculatedTotalPages = Math.ceil(totalItems.value / perPage.value)
  
  // Si pas de données ou une seule page, ne pas afficher la pagination
  if (calculatedTotalPages <= 1) {
    return []
  }

  const pages = []
  const maxDisplayed = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let end = Math.min(calculatedTotalPages, start + maxDisplayed - 1)

  if (end - start + 1 < maxDisplayed) {
    start = Math.max(1, end - maxDisplayed + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Computed property pour contrôler l'affichage de la pagination
const showPagination = computed(() => {
  return totalItems.value > perPage.value
})

// Fonction pour changer de page
async function changePage(page) {
  if (page >= 1 && page <= lastPage.value && page !== currentPage.value) {
    try {
      isLoading.value = true
      currentPage.value = page
      await loadBiens()
    } catch (error) {
      console.error('Erreur lors du changement de page:', error)
    } finally {
      isLoading.value = false
    }
  }
}

// Chargement des biens avec filtres
async function loadBiens() {
  try {
    isLoading.value = true
    error.value = null

    const filters = {
      project: searchProjet.value, // Fix: backend expects 'project', not 'projet'
      type: selectedType.value !== 'Tout' ? selectedType.value : '',
      status: mapStatusToBackend(selectedStatus.value)
    }
    
    const response = await bienService.getBiens(currentPage.value, perPage.value, filters)
    
    if (response && response.success && response.data && response.data.data) {
      biens.value = Array.isArray(response.data.data) ? response.data.data : []
      totalItems.value = response.data.total || 0
      currentPage.value = response.data.current_page || 1
      lastPage.value = response.data.last_page || 1
      perPage.value = response.data.per_page || 10
    } else {
      biens.value = []
      totalItems.value = 0
      currentPage.value = 1
      lastPage.value = 1
      perPage.value = 10
    }

  } catch (err) {
    if (err.response && err.response.status === 401) {
      error.value = "Vous devez être connecté pour voir les biens. Veuillez vous connecter."
    } else if (err.response && err.response.status === 403) {
      error.value = "Vous n'avez pas les permissions pour voir les biens."
    } else {
      error.value = "Erreur lors du chargement des biens. Veuillez réessayer."
    }
    console.error('Erreur lors du chargement des biens:', err)
  } finally {
    isLoading.value = false
  }
}

// Map French status values to backend values
function mapStatusToBackend(status) {
  const statusMap = {
    'Toutes': '',
    'Occupés': 'rented',
    'Vacantes': 'available'
  }
  return statusMap[status] || ''
}

// Map backend status values to French labels
function mapStatusToFrontend(status) {
  const statusMap = {
    'rented': 'Occupé',
    'available': 'Vacant',
    'maintenance': 'Maintenance'
  }
  return statusMap[status] || status
}

// Chargement initial
onMounted(async () => {
  await loadBiens()
})

// Gestion des uploads
async function handleFileUpload(event, type) {
  const file = event.target.files[0]
  if (!file) return

  try {
    if (type === 'plan') {
      newBien.value.plan = file
    } else if (type === 'temoin') {
      newBien.value.temoin = file
    }
  } catch (error) {
    alert(`Erreur lors de l'upload du ${type}`)
    console.error(error)
  }
}

// Ajout d'un nouveau bien
async function addBien() {
  try {
    isLoading.value = true

    // Validation des champs requis
    if (!newBien.value.ref || !newBien.value.projet || !newBien.value.type) {
      alert("Veuillez remplir tous les champs obligatoires")
      return
    }

    const response = await bienService.createBien(newBien.value)
    
    // Ajouter le nouveau bien à la liste (ensure biens.value is an array)
    if (!Array.isArray(biens.value)) {
      biens.value = []
    }
    biens.value.push(response)
    
    // Réinitialiser le formulaire
    newBien.value = {
      ref: '',
      projet: '',
      type: '',
      surface: null,
      terasse: null,
      box: null,
      plan: null,
      temoin: null
    }
    
    showAddModal.value = false
    alert("Bien ajouté avec succès")
  } catch (error) {
    alert("Erreur lors de l'ajout du bien")
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

// Upload des documents
async function uploadPlan() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.pdf,.jpg,.jpeg,.png'
  input.onchange = (e) => handleFileUpload(e, 'plan')
  input.click()
}

async function uploadTemoin() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.pdf,.jpg,.jpeg,.png'
  input.onchange = (e) => handleFileUpload(e, 'temoin')
  input.click()
}

// Nouvelles fonctions pour les actions
function formatPrice(price) {
  if (!price) return 'N/A'
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price)
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR')
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'N/A'
  }
}

function getTypeLabel(type) {
  const typeMap = {
    'apartment': 'Appartement',
    'shop': 'Magasin', 
    'office': 'Bureau',
    'house': 'Maison',
    'land': 'Terrain'
  }
  return typeMap[type] || type
}


</script>

<style scoped>
.gestion-biens-container {
  margin-left: 270px; /* Largeur de la sidebar */
  padding: 24px;
  background: #f5f7fa;
  height: calc(100vh - 70px); /* Hauteur totale moins la navbar */
  margin-top: 70px; /* Hauteur de la navbar */
  overflow: hidden; /* Empêcher le scroll de la page */
  display: flex;
  flex-direction: column;
  width: calc(100% - 270px);
  max-width: calc(100% - 270px);
  box-sizing: border-box;
}

.gestion-biens-header {
  margin-bottom: 24px;
  flex-shrink: 0; /* Empêcher l'en-tête de rétrécir */
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.header-icon {
  width: 38px;
  height: 38px;
  object-fit: contain;
}

h1 {
  font-size: 2rem;
  color: #22305a;
  font-weight: 700;
  margin: 0;
}

.header-filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-select {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  width: 250px;
  background: white;
}

.type-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.filter-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #22305a;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: #f0f2f5;
}

.filter-btn.active {
  background: #4CAF50; /* Vert */
  color: white;
  border-color: #4CAF50;
}

.status-tabs {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  justify-content: center;
  max-width: 1200px;
  margin: 24px auto;
}

.tab-btn {
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  background: #f0f2f5;
  color: #666;
  min-width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.tab-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.tab-btn.active {
  background: #4CAF50;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.tab-btn.occupied {
  background: white;
  color: #c00;
  border: 2px solid #c00;
}

.tab-btn.occupied.active {
  background: #c00;
  color: white;
  border: 2px solid #c00;
  box-shadow: 0 4px 12px rgba(204, 0, 0, 0.3);
}

.tab-btn.vacant {
  background: white;
  color: #1656c1;
  border: 2px solid #1656c1;
}

.tab-btn.vacant.active {
  background: #1656c1;
  color: white;
  border: 2px solid #1656c1;
  box-shadow: 0 4px 12px rgba(22, 86, 193, 0.3);
}

.count {
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.tab-btn.active .count {
  background: white;
  color: #4CAF50;
}

.tab-btn.occupied .count {
  background: white;
  color: #c00;
}

.tab-btn.occupied.active .count {
  background: white;
  color: #c00;
  box-shadow: 0 2px 4px rgba(204, 0, 0, 0.2);
}

.tab-btn.vacant .count {
  background: white;
  color: #1656c1;
}

.tab-btn.vacant.active .count {
  background: white;
  color: #1656c1;
  box-shadow: 0 2px 4px rgba(22, 86, 193, 0.2);
}

.biens-table {
  background: white;
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.table-header {
  display: grid;
  grid-template-columns: 0.5fr 1fr 0.8fr 0.8fr 1fr 1.5fr 0.8fr 0.8fr 0.8fr 1.5fr 1fr; /* Compact responsive columns */
  background: #bfc7d5;
  padding: 12px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.header-cell {
  color: #22305a;
  font-weight: 700;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-body {
  overflow: auto;
  flex: 1;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #22305a;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-body.loading .table-row {
  opacity: 0.5;
}

.table-row {
  display: grid;
  grid-template-columns: 0.5fr 1fr 0.8fr 0.8fr 1fr 1.5fr 0.8fr 0.8fr 0.8fr 1.5fr 1fr; /* Same responsive columns as header */
  padding: 12px;
  border-bottom: 1px solid #eee;
  align-items: center;
  transition: opacity 0.3s ease;
  min-height: 50px;
}

.table-row.phantom {
  opacity: 0.1;
  pointer-events: none;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #666;
  font-style: italic;
}

/* Ajuster la taille des cellules */
.cell {
  font-size: 0.85rem;
  color: #22305a;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell:hover {
  white-space: normal;
  overflow: visible;
  z-index: 10;
  position: relative;
  background: white;
  padding: 8px 10px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
}

.status-badge.occupied {
  background: #ffebee;
  color: #c00;
}

.status-badge.vacant {
  background: #e3f2fd;
  color: #1656c1;
}

.view-btn {
  background: none;
  border: none;
  color: #22305a;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-btn:hover {
  background: #f0f2f5;
}

.add-bien-btn {
  position: absolute; /* Au lieu de fixed */
  bottom: 32px;
  right: 32px;
  background: #22305a;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(34, 48, 90, 0.2);
  transition: all 0.2s;
}

.add-bien-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(34, 48, 90, 0.3);
}

.btn-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.add-bien-modal {
  background: white;
  border-radius: 12px;
  width: 500px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background: #22305a;
  color: white;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #22305a;
  font-weight: 600;
  margin-bottom: 8px;
}

.form-group input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

.type-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.type-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #22305a;
  cursor: pointer;
  transition: all 0.2s;
}

.type-btn.active {
  background: #22305a;
  color: white;
  border-color: #22305a;
}

.documents {
  display: flex;
  gap: 24px;
}

.doc-link {
  display: flex;
  align-items: center;
  gap: 8px;
}

.doc-link a {
  color: #22305a;
  text-decoration: none;
  font-weight: 500;
}

.doc-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.modal-footer {
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.submit-btn {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 10px 32px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover {
  background: #45a049;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.pagination-info {
  color: #666;
  font-size: 0.9rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding: 16px;
}

.pagination-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #22305a;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #2c3e6e;
  transform: translateY(-1px);
}

.pagination-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 8px;
}

.page-number {
  width: 36px;
  height: 36px;
  border: 2px solid #22305a;
  border-radius: 6px;
  background: white;
  color: #22305a;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-number:hover {
  background: #f0f2f5;
}

.page-number.active {
  background: #22305a;
  color: white;
}

.filter-input {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  width: 250px;
  background: white;
  transition: all 0.2s ease;
}

.filter-input:focus {
  border-color: #22305a;
  box-shadow: 0 0 0 2px rgba(34, 48, 90, 0.1);
  outline: none;
}

.error-message {
  background: #fee;
  color: #c00;
  padding: 12px;
  border-radius: 6px;
  margin: 12px 0;
  text-align: center;
}

/* Styles pour les nouvelles colonnes */
.id-value {
  font-weight: 600;
  color: #22305a;
  font-size: 0.9rem;
}

.project-name {
  font-weight: 600;
  color: #22305a;
  font-size: 0.95rem;
}

.type-badge {
  background: #f3e5f5;
  color: #7b1fa2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-block;
}

.surface-value {
  font-weight: 600;
  color: #22305a;
  font-size: 0.9rem;
}

.price-value {
  font-weight: 600;
  color: #2e7d32;
  font-size: 0.95rem;
}

.address-main {
  font-weight: 500;
  color: #22305a;
  font-size: 0.9rem;
}

.city-value {
  font-weight: 500;
  color: #22305a;
  font-size: 0.9rem;
}

.postal-value {
  font-weight: 500;
  color: #666;
  font-size: 0.9rem;
}

.description-text {
  color: #555;
  font-size: 0.85rem;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-value {
  font-weight: 500;
  color: #666;
  font-size: 0.85rem;
}


</style> 